import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import api, { casesApi, activityDaysApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { 
    ArrowLeft,
    FolderOpen,
    Calendar,
    Plus,
    Download,
    Edit,
    Trash2,
    Clock,
    Users,
    ChevronRight,
    AlertCircle,
    User,
    Car,
    MapPin,
    Home,
    FileText,
    Image,
    Upload,
    Camera,
    Tag,
    AlertTriangle,
    Globe,
    Instagram,
    Linkedin,
    Cloud,
    CloudUpload,
    Check
} from 'lucide-react';
import { formatDate, formatMinutesToHours, getStatusColor, getStatusLabel } from '../lib/utils';
import CreateActivityDayModal from '../components/CreateActivityDayModal';
import EditCaseModal from '../components/EditCaseModal';
import CaseImagesManager from '../components/CaseImagesManager';
import { ReportDownloadProgress } from '../components/ReportDownloadProgress';
import { PdfViewerModal } from '../components/PdfViewerModal';
import { ReportPhotoCheckDialog } from '../components/ReportPhotoCheckDialog';
import CaseAgentManager from '../components/CaseAgentManager';
import AgentHoursSummary from '../components/AgentHoursSummary';
import InvestigatedPhotosManager from '../components/InvestigatedPhotosManager';
import ImportFromOnboardingDialog from '../components/ImportFromOnboardingDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Mappatura tipologie
const CASE_TYPE_LABELS = {
    'infedelta_coniugale': 'Infedeltà coniugale',
    'concorrenza_sleale': 'Concorrenza sleale',
    'falsa_malattia': 'Falsa malattia',
    'abuso_104': 'Abuso 104',
    'altro': 'Altro'
};

// Componente Dialog di Conferma Eliminazione
function DeleteConfirmDialog({ isOpen, onClose, onConfirm, caseNumber }) {
    const [confirmText, setConfirmText] = useState('');
    const [deleting, setDeleting] = useState(false);
    
    if (!isOpen) return null;
    
    const handleConfirm = async () => {
        if (confirmText !== 'ELIMINA') return;
        setDeleting(true);
        await onConfirm();
        setDeleting(false);
    };
    
    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0A0A0A] border border-red-500/50 w-full max-w-md p-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-red-500/20 border border-red-500 flex items-center justify-center">
                        <AlertTriangle className="w-6 h-6 text-red-500" />
                    </div>
                    <div>
                        <h3 className="text-white font-bold text-lg">Elimina Caso</h3>
                        <p className="text-white/60 text-sm">{caseNumber}</p>
                    </div>
                </div>
                
                <div className="mb-6 space-y-3">
                    <p className="text-white/80">
                        Sei sicuro di voler eliminare questo caso? Questa azione è <span className="text-red-500 font-bold">irreversibile</span> e cancellerà:
                    </p>
                    <ul className="text-white/60 text-sm list-disc list-inside space-y-1">
                        <li>Tutti i dati del caso</li>
                        <li>Tutte le giornate di attività</li>
                        <li>Tutti i media e le immagini caricate</li>
                    </ul>
                </div>
                
                <div className="mb-6">
                    <label className="text-white/60 text-sm block mb-2">
                        Digita <span className="text-red-500 font-mono font-bold">ELIMINA</span> per confermare:
                    </label>
                    <Input
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder="ELIMINA"
                        data-testid="delete-confirm-input"
                        className="bg-black border-red-500/50 text-white focus:border-red-500 rounded-none font-mono"
                    />
                </div>
                
                <div className="flex justify-end gap-3">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        disabled={deleting}
                        className="border-white/20 text-white hover:bg-white/10 rounded-none"
                    >
                        ANNULLA
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={confirmText !== 'ELIMINA' || deleting}
                        data-testid="confirm-delete-btn"
                        className="bg-red-500 text-white hover:bg-red-600 rounded-none disabled:opacity-50"
                    >
                        {deleting ? 'ELIMINAZIONE...' : 'ELIMINA CASO'}
                    </Button>
                </div>
            </div>
        </div>
    );
}

// Componente inline per la foto indagato
function InvestigatedPhotoUploader({ caseId, caseData, isAdmin, onUpdate }) {
    const [photoUrl, setPhotoUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const token = localStorage.getItem('token');

    useEffect(() => {
        let objectUrl = null;
        let mounted = true;
        
        const loadPhoto = async () => {
            if (caseData.investigated_photo) {
                setLoading(true);
                try {
                    const response = await axios.get(
                        `${API_URL}/cases/${caseId}/investigated-photo/${caseData.investigated_photo.id}`,
                        {
                            headers: { Authorization: `Bearer ${token}` },
                            responseType: 'blob'
                        }
                    );
                    if (mounted) {
                        objectUrl = URL.createObjectURL(response.data);
                        setPhotoUrl(objectUrl);
                    }
                } catch (err) {
                    // Silent fail for photo loading errors
                } finally {
                    if (mounted) setLoading(false);
                }
            } else {
                setPhotoUrl(null);
                setLoading(false);
            }
        };
        
        loadPhoto();
        
        return () => {
            mounted = false;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseId, caseData.investigated_photo?.id, token]);

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        const scrollPos = window.scrollY;
        
        try {
            setUploading(true);
            setError('');
            await casesApi.uploadInvestigatedPhoto(caseId, file);
            onUpdate();
            setTimeout(() => window.scrollTo(0, scrollPos), 100);
        } catch (err) {
            setError('Errore nel caricamento');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Eliminare la foto?')) return;
        
        const scrollPos = window.scrollY;
        
        try {
            await casesApi.deleteInvestigatedPhoto(caseId);
            onUpdate();
            setTimeout(() => window.scrollTo(0, scrollPos), 100);
        } catch (err) {
            setError('Errore eliminazione');
        }
    };

    // Mostra spinner durante il caricamento
    if (loading) {
        return (
            <div className="w-24 h-24 border border-white/20 flex items-center justify-center bg-black/30">
                <div className="w-6 h-6 border-2 border-[#00FF41]/30 border-t-[#00FF41] rounded-full animate-spin" />
            </div>
        );
    }

    // Mostra foto se caricata
    if (caseData.investigated_photo && photoUrl) {
        return (
            <div className="relative group inline-block">
                <div className="w-24 h-24 border border-white/20 overflow-hidden">
                    <img
                        src={photoUrl}
                        alt="Foto indagato"
                        className="w-full h-full object-cover"
                    />
                </div>
                {isAdmin && (
                    <button
                        onClick={handleDelete}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Elimina foto"
                    >
                        <Trash2 className="w-3 h-3" />
                    </button>
                )}
            </div>
        );
    }

    // Mostra pulsante upload per admin
    if (isAdmin) {
        return (
            <div className="flex items-center gap-3">
                <div className="w-20 h-20 border border-dashed border-white/30 flex items-center justify-center bg-black/30">
                    <Camera className="w-6 h-6 text-white/30" />
                </div>
                <label className="cursor-pointer">
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleUpload}
                        className="hidden"
                        data-testid="upload-investigated-photo"
                    />
                    <div className={`px-3 py-2 bg-[#00FF41] text-black text-xs font-bold uppercase hover:bg-[#00CC33] transition-all flex items-center gap-1 ${uploading ? 'opacity-50' : ''}`}>
                        <Upload className="w-3 h-3" />
                        {uploading ? 'CARICAMENTO...' : 'CARICA'}
                    </div>
                </label>
                {error && <span className="text-red-500 text-xs">{error}</span>}
            </div>
        );
    }

    return (
        <div className="w-20 h-20 border border-dashed border-white/20 flex items-center justify-center bg-black/20">
            <Camera className="w-6 h-6 text-white/20" />
        </div>
    );
}

export default function CaseDetail() {
    const { caseId } = useParams();
    const navigate = useNavigate();
    const { user, isAdmin } = useAuth();
    const [caseData, setCaseData] = useState(null);
    const [activityDays, setActivityDays] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateDay, setShowCreateDay] = useState(false);
    const [showEditCase, setShowEditCase] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [downloading, setDownloading] = useState(false);
    
    // Stato Dropbox
    const [uploadingDropbox, setUploadingDropbox] = useState(false);
    const [dropboxSuccess, setDropboxSuccess] = useState(false);
    const [dropboxError, setDropboxError] = useState('');

    useEffect(() => {
        loadData();
    }, [caseId]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [caseRes, daysRes] = await Promise.all([
                casesApi.get(caseId),
                activityDaysApi.list(caseId)
            ]);
            setCaseData(caseRes.data);
            setActivityDays(daysRes.data);
        } catch (err) {
            setError('Errore nel caricamento del caso');
        } finally {
            setLoading(false);
        }
    };

    const [reportProgress, setReportProgress] = useState(null);
    const reportAbortRef = useRef(null);
    const [photoCheck, setPhotoCheck] = useState(null);
    const [checkingPhotos, setCheckingPhotos] = useState(false);

    // Verifica le foto prima di generare il report; se ci sono problemi chiede conferma
    const withPhotoCheck = (action) => async () => {
        if (checkingPhotos) return;
        setCheckingPhotos(true);
        try {
            const { data } = await api.get(`/cases/${caseId}/report-photo-check`, { timeout: 0 });
            if (data.problems?.length) {
                setPhotoCheck({ ...data, action });
                return;
            }
        } catch (e) {
            // se il controllo fallisce non bloccare la generazione
        } finally {
            setCheckingPhotos(false);
        }
        action();
    };

    const handleDownloadReport = async () => {
        if (reportProgress) return;
        const controller = new AbortController();
        reportAbortRef.current = controller;
        setReportProgress({ phase: 'generating', loaded: 0, total: 0 });
        try {
            const response = await api.get(`/cases/${caseId}/report`, {
                responseType: 'blob',
                timeout: 0,
                signal: controller.signal,
                onDownloadProgress: (e) => setReportProgress({ phase: 'downloading', loaded: e.loaded, total: e.total || 0 }),
            });
            const disposition = response.headers['content-disposition'] || '';
            const match = disposition.match(/filename="?([^";]+)"?/);
            const filename = match ? match[1] : `Report_${caseData?.protocol_number || caseId}.docx`;
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 100);
        } catch (err) {
            if (err.code !== 'ERR_CANCELED') {
                let msg = 'Errore durante il download del report';
                if (err.response?.data instanceof Blob) {
                    try { msg = JSON.parse(await err.response.data.text()).detail || msg; } catch (e) { /* risposta non JSON */ }
                }
                alert(msg);
            }
        } finally {
            reportAbortRef.current = null;
            setReportProgress(null);
        }
    };

    const [showInvestigatedPdf, setShowInvestigatedPdf] = useState(false);
    const handleDownloadInvestigatedPdf = () => setShowInvestigatedPdf(true);
    
    const [uploadingInvestigatedDropbox, setUploadingInvestigatedDropbox] = useState(false);
    const [investigatedDropboxSuccess, setInvestigatedDropboxSuccess] = useState(false);
    const [investigatedDropboxError, setInvestigatedDropboxError] = useState('');
    const [showImportOnboarding, setShowImportOnboarding] = useState(false);
    
    const handleUploadInvestigatedToDropbox = async () => {
        try {
            setUploadingInvestigatedDropbox(true);
            setInvestigatedDropboxError('');
            setInvestigatedDropboxSuccess(false);
            
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/investigated-pdf/upload-dropbox`,
                {},
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.data.success) {
                setInvestigatedDropboxSuccess(true);
                setTimeout(() => setInvestigatedDropboxSuccess(false), 5000);
                loadData();
            } else {
                setInvestigatedDropboxError(response.data.error || 'Errore upload');
            }
        } catch (error) {
            setInvestigatedDropboxError(error.response?.data?.detail || 'Errore durante upload su Dropbox');
        } finally {
            setUploadingInvestigatedDropbox(false);
        }
    };
    
    const handleUploadToDropbox = async () => {
        try {
            setUploadingDropbox(true);
            setDropboxError('');
            setDropboxSuccess(false);
            
            const response = await casesApi.uploadReportToDropbox(caseId);
            
            if (response.data.success) {
                setDropboxSuccess(true);
                setTimeout(() => setDropboxSuccess(false), 5000);
                // Ricarica i dati per aggiornare il link Dropbox
                loadData();
            } else {
                setDropboxError(response.data.error || 'Errore sconosciuto');
            }
        } catch (err) {
            const errorMsg = err.response?.data?.detail || 'Errore nel caricamento su Dropbox';
            setDropboxError(errorMsg);
            setTimeout(() => setDropboxError(''), 5000);
        } finally {
            setUploadingDropbox(false);
        }
    };

    const handleDeleteCase = async () => {
        try {
            await casesApi.delete(caseId);
            navigate(isAdmin ? '/admin' : '/dashboard');
        } catch (err) {
            setError('Errore nell\'eliminazione del caso');
            setShowDeleteDialog(false);
        }
    };

    const handleDayCreated = (newDay) => {
        setActivityDays([...activityDays, newDay].sort((a, b) => a.date.localeCompare(b.date)));
        setShowCreateDay(false);
    };

    const handleCaseUpdated = (updatedCase) => {
        setCaseData(updatedCase);
        setShowEditCase(false);
    };

    // Calculate totals
    const totalServiceMinutes = activityDays.reduce((sum, day) => sum + (day.total_service_minutes || 0), 0);
    const maxOperators = Math.max(...activityDays.map(d => d.operators_count || 0), 0);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#050505] flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-[#00FF41]/30 border-t-[#00FF41] rounded-full animate-spin" />
            </div>
        );
    }

    if (!caseData) {
        return (
            <div className="min-h-screen bg-[#050505] flex items-center justify-center">
                <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <p className="text-white/60">Caso non trovato</p>
                    <Button
                        onClick={() => navigate(-1)}
                        className="mt-4 bg-[#00FF41] text-black hover:bg-[#00CC33] rounded-none"
                    >
                        Torna indietro
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#050505]">
            <div className="noise-overlay" />
            <ReportDownloadProgress progress={reportProgress} onCancel={() => reportAbortRef.current?.abort()} />
            <ReportPhotoCheckDialog
                result={photoCheck}
                onCancel={() => setPhotoCheck(null)}
                onProceed={() => { const run = photoCheck.action; setPhotoCheck(null); run(); }}
                onOpenDay={(dayId) => navigate(`/cases/${caseId}/days/${dayId}`)}
            />
            {showInvestigatedPdf && (
                <PdfViewerModal
                    apiPath={`/cases/${caseId}/investigated-pdf`}
                    title="Scheda Indagato"
                    filename={`Scheda_Indagato_${(caseData?.investigated?.name || 'indagato').replace(/ /g, '_')}_${caseData?.protocol_number || caseId}.pdf`}
                    onClose={() => setShowInvestigatedPdf(false)}
                />
            )}

            {/* Header */}
            <header className="bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-4">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(isAdmin ? '/admin' : '/dashboard')}
                                className="text-white/60 hover:text-white hover:bg-white/10"
                            >
                                <ArrowLeft className="w-4 h-4" />
                            </Button>
                            <div className="flex items-center gap-2">
                                <FolderOpen className="w-5 h-5 text-[#00FF41]" />
                                <span className="font-mono text-[#00FF41]">{caseData.protocol_number || caseData.case_number}</span>
                            </div>
                        </div>
                        
                        {/* Pulsanti Azioni - Layout organizzato */}
                        <div className="flex items-center gap-4">
                            {/* Gruppo Report */}
                            {isAdmin && (
                                <div className="flex items-center gap-2 border-r border-white/20 pr-4">
                                    <button
                                        type="button"
                                        onClick={withPhotoCheck(handleDownloadReport)}
                                        disabled={!!reportProgress || checkingPhotos}
                                        data-testid="download-report-btn"
                                        className="inline-flex items-center justify-center bg-[#00FF41] text-black hover:bg-[#00CC33] hover:shadow-neon rounded-none px-3 py-1.5 font-medium text-xs disabled:opacity-60 disabled:cursor-wait"
                                        title="Scarica il report Word"
                                    >
                                        {checkingPhotos ? (
                                            <div data-testid="photo-check-loading" className="w-3.5 h-3.5 mr-1.5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                                        ) : (
                                            <Download className="w-3.5 h-3.5 mr-1.5" />
                                        )}
                                        REPORT
                                    </button>
                                    <Button
                                        onClick={withPhotoCheck(handleUploadToDropbox)}
                                        disabled={uploadingDropbox || checkingPhotos}
                                        data-testid="upload-dropbox-btn"
                                        size="sm"
                                        className={`rounded-none px-3 py-1.5 font-medium text-xs h-auto ${
                                            dropboxSuccess 
                                                ? 'bg-green-500 text-white' 
                                                : 'bg-blue-500 text-white hover:bg-blue-600'
                                        }`}
                                        title="Carica su Dropbox"
                                    >
                                        {uploadingDropbox ? (
                                            <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        ) : dropboxSuccess ? (
                                            <Check className="w-3.5 h-3.5" />
                                        ) : (
                                            <CloudUpload className="w-3.5 h-3.5" />
                                        )}
                                    </Button>
                                </div>
                            )}
                            
                            {/* Gruppo Modifica/Elimina */}
                            {isAdmin && (
                                <div className="flex items-center gap-1">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setShowEditCase(true)}
                                        data-testid="edit-case-btn"
                                        className="text-white/60 hover:text-[#00FF41] hover:bg-[#00FF41]/10 h-8 w-8 p-0"
                                        title="Modifica caso"
                                    >
                                        <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setShowDeleteDialog(true)}
                                        data-testid="delete-case-btn"
                                        className="text-red-500/60 hover:text-red-500 hover:bg-red-500/10 h-8 w-8 p-0"
                                        title="Elimina caso"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {error && (
                    <div className="bg-red-500/10 border border-red-500 p-4 mb-6 flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-red-500" />
                        <span className="text-red-500">{error}</span>
                    </div>
                )}
                
                {dropboxError && (
                    <div className="bg-red-500/10 border border-red-500 p-4 mb-6 flex items-center gap-2">
                        <Cloud className="w-5 h-5 text-red-500" />
                        <span className="text-red-500">Dropbox: {dropboxError}</span>
                    </div>
                )}
                
                {dropboxSuccess && (
                    <div className="bg-green-500/10 border border-green-500 p-4 mb-6 flex items-center gap-2">
                        <Check className="w-5 h-5 text-green-500" />
                        <span className="text-green-500">Report caricato su Dropbox con successo!</span>
                        {caseData?.dropbox_link && (
                            <a 
                                href={caseData.dropbox_link} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-400 hover:underline ml-2"
                            >
                                Apri su Dropbox →
                            </a>
                        )}
                    </div>
                )}

                {/* Case Info Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                    {/* Client Info - Solo Admin */}
                    {isAdmin && (
                        <div className="bg-[#0A0A0A] border border-white/10 p-6">
                            <h3 className="font-heading text-lg font-bold text-white mb-4 flex items-center gap-2">
                                <User className="w-5 h-5 text-[#00FF41]" />
                                Informazioni Cliente
                            </h3>
                            <div className="space-y-3">
                                <div>
                                    <p className="text-white/40 text-xs uppercase">Nome Cliente</p>
                                    <p className="text-white font-medium">{caseData.client_name}</p>
                                </div>
                                {caseData.client_address && (
                                    <div>
                                        <p className="text-white/40 text-xs uppercase">Indirizzo</p>
                                        <p className="text-white/80">{caseData.client_address}</p>
                                    </div>
                                )}
                                {caseData.client_email && (
                                    <div>
                                        <p className="text-white/40 text-xs uppercase">Email</p>
                                        <p className="text-white/80 font-mono text-sm">{caseData.client_email}</p>
                                    </div>
                                )}
                                <div>
                                    <p className="text-white/40 text-xs uppercase">Stato</p>
                                    <span className={`badge-neon ${getStatusColor(caseData.status)}`}>
                                        {getStatusLabel(caseData.status)}
                                    </span>
                                </div>
                                {caseData.case_type && (
                                    <div>
                                        <p className="text-white/40 text-xs uppercase">Tipologia</p>
                                        <span className="text-white flex items-center gap-1">
                                            <Tag className="w-3 h-3 text-[#00FF41]" />
                                            {CASE_TYPE_LABELS[caseData.case_type] || caseData.case_type}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Investigated Person */}
                    <div className="bg-[#0A0A0A] border border-white/10 p-6 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-4 flex-wrap">
                            <h3 className="font-heading text-lg font-bold text-white flex items-center gap-2 min-w-0">
                                <FileText className="w-5 h-5 text-[#00FF41] flex-shrink-0" />
                                <span className="truncate">Informazioni Indagato</span>
                            </h3>
                            <div className="flex items-center gap-2 flex-wrap justify-end">
                                {isAdmin && (
                                    <button
                                        type="button"
                                        onClick={() => setShowImportOnboarding(true)}
                                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-mono uppercase bg-[#00FF41]/15 text-[#00FF41] border border-[#00FF41]/40 hover:bg-[#00FF41]/25 transition-colors whitespace-nowrap"
                                        data-testid="import-from-onboarding-btn"
                                        title="Importa scheda indagato da Smart Onboarding"
                                    >
                                        <Download className="w-3.5 h-3.5" />
                                        Importa
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={handleDownloadInvestigatedPdf}
                                    data-testid="download-investigated-pdf-btn"
                                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-mono uppercase bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:bg-blue-500/30 transition-colors whitespace-nowrap"
                                >
                                    <Download className="w-3.5 h-3.5" />
                                    PDF
                                </button>
                                {isAdmin && (
                                    <button
                                        type="button"
                                        onClick={handleUploadInvestigatedToDropbox}
                                        data-testid="upload-investigated-dropbox-btn"
                                        disabled={uploadingInvestigatedDropbox}
                                        className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-mono uppercase border transition-colors whitespace-nowrap ${
                                            investigatedDropboxSuccess 
                                                ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                                                : 'bg-purple-500/20 text-purple-400 border-purple-500/30 hover:bg-purple-500/30'
                                        } ${uploadingInvestigatedDropbox ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        <Cloud className="w-3.5 h-3.5" />
                                        {uploadingInvestigatedDropbox ? '...' : investigatedDropboxSuccess ? 'Caricato!' : 'Dropbox'}
                                    </button>
                                )}
                            </div>
                        </div>
                        
                        {investigatedDropboxError && (
                            <div className="mb-4 p-2 bg-red-500/20 border border-red-500/30 text-red-400 text-xs">
                                {investigatedDropboxError}
                            </div>
                        )}
                        
                        {/* Foto Indagato - con supporto multiple foto e ingrandimento */}
                        <div className="mb-4 pb-4 border-b border-white/10">
                            <p className="text-white/40 text-xs uppercase mb-2">Foto</p>
                            <InvestigatedPhotosManager 
                                caseId={caseId}
                                caseData={caseData}
                                isAdmin={isAdmin}
                                onUpdate={loadData}
                                showUpload={false}
                            />
                            {isAdmin && (
                                <p className="text-white/30 text-xs mt-2">
                                    Per aggiungere foto usa la sezione Modifica
                                </p>
                            )}
                        </div>
                        
                        <div className="space-y-3">
                            <div className="flex items-start gap-2">
                                <User className="w-4 h-4 text-[#00FF41] mt-1 flex-shrink-0" />
                                <div>
                                    <p className="text-white/40 text-xs uppercase">Nome</p>
                                    <p className="text-white">{caseData.investigated?.name || '-'}</p>
                                </div>
                            </div>
                            {caseData.investigated?.gender && (
                                <div className="flex items-start gap-2">
                                    <User className="w-4 h-4 text-[#00FF41] mt-1 flex-shrink-0" />
                                    <div>
                                        <p className="text-white/40 text-xs uppercase">Sesso</p>
                                        <p className="text-white/80">{caseData.investigated.gender === 'M' ? 'Maschile' : 'Femminile'}</p>
                                    </div>
                                </div>
                            )}
                            <div className="flex items-start gap-2">
                                <Car className="w-4 h-4 text-[#00FF41] mt-1 flex-shrink-0" />
                                <div>
                                    <p className="text-white/40 text-xs uppercase">Automezzo</p>
                                    <p className="text-white/80">{caseData.investigated?.vehicle || '-'}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-2">
                                <Calendar className="w-4 h-4 text-[#00FF41] mt-1 flex-shrink-0" />
                                <div>
                                    <p className="text-white/40 text-xs uppercase">Data di Nascita</p>
                                    <p className="text-white/80">{caseData.investigated?.birth_date ? formatDate(caseData.investigated.birth_date) : '-'}</p>
                                </div>
                            </div>
                            {caseData.investigated?.birth_place && (
                                <div className="flex items-start gap-2">
                                    <MapPin className="w-4 h-4 text-[#00FF41] mt-1 flex-shrink-0" />
                                    <div>
                                        <p className="text-white/40 text-xs uppercase">Luogo di Nascita</p>
                                        <p className="text-white/80">{caseData.investigated.birth_place}</p>
                                    </div>
                                </div>
                            )}
                            {caseData.investigated?.fiscal_code && (
                                <div className="flex items-start gap-2">
                                    <FileText className="w-4 h-4 text-[#00FF41] mt-1 flex-shrink-0" />
                                    <div>
                                        <p className="text-white/40 text-xs uppercase">Codice Fiscale</p>
                                        <p className="text-white/80 font-mono text-sm">{caseData.investigated.fiscal_code}</p>
                                    </div>
                                </div>
                            )}
                            <div className="flex items-start gap-2">
                                <MapPin className="w-4 h-4 text-[#00FF41] mt-1 flex-shrink-0" />
                                <div>
                                    <p className="text-white/40 text-xs uppercase">Residenza</p>
                                    <p className="text-white/80">{caseData.investigated?.address || '-'}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-2">
                                <Home className="w-4 h-4 text-[#00FF41] mt-1 flex-shrink-0" />
                                <div>
                                    <p className="text-white/40 text-xs uppercase">Domicilio</p>
                                    <p className="text-white/80">{caseData.investigated?.domicile || '-'}</p>
                                </div>
                            </div>
                            {(caseData.investigated?.workplace_company || caseData.investigated?.workplace_address) && (
                                <div className="flex items-start gap-2">
                                    <Tag className="w-4 h-4 text-[#00FF41] mt-1 flex-shrink-0" />
                                    <div>
                                        <p className="text-white/40 text-xs uppercase">Luogo di Lavoro</p>
                                        <p className="text-white/80">
                                            {caseData.investigated.workplace_company}
                                            {caseData.investigated.workplace_company && caseData.investigated.workplace_address && ' - '}
                                            {caseData.investigated.workplace_address}
                                        </p>
                                    </div>
                                </div>
                            )}
                            
                            {/* Social Network Section */}
                            {(caseData.investigated?.social?.facebook || 
                              caseData.investigated?.social?.instagram || 
                              caseData.investigated?.social?.tiktok || 
                              caseData.investigated?.social?.linkedin || 
                              caseData.investigated?.social?.altro) && (
                                <div className="pt-3 mt-3 border-t border-white/10">
                                    <p className="text-white/40 text-xs uppercase mb-3 flex items-center gap-1">
                                        <Globe className="w-3 h-3" /> Social Network
                                    </p>
                                    <div className="grid grid-cols-2 gap-2">
                                        {caseData.investigated.social.facebook && (
                                            <a 
                                                href={caseData.investigated.social.facebook.startsWith('http') ? caseData.investigated.social.facebook : `https://facebook.com/${caseData.investigated.social.facebook}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                                            >
                                                <div className="w-6 h-6 bg-blue-600/20 rounded flex items-center justify-center">
                                                    <span className="text-xs font-bold">f</span>
                                                </div>
                                                <span className="truncate">{caseData.investigated.social.facebook}</span>
                                            </a>
                                        )}
                                        {caseData.investigated.social.instagram && (
                                            <a 
                                                href={caseData.investigated.social.instagram.startsWith('http') ? caseData.investigated.social.instagram : `https://instagram.com/${caseData.investigated.social.instagram}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-2 text-sm text-pink-400 hover:text-pink-300 transition-colors"
                                            >
                                                <Instagram className="w-5 h-5" />
                                                <span className="truncate">{caseData.investigated.social.instagram}</span>
                                            </a>
                                        )}
                                        {caseData.investigated.social.tiktok && (
                                            <a 
                                                href={caseData.investigated.social.tiktok.startsWith('http') ? caseData.investigated.social.tiktok : `https://tiktok.com/@${caseData.investigated.social.tiktok}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-2 text-sm text-white/80 hover:text-white transition-colors"
                                            >
                                                <div className="w-6 h-6 bg-white/10 rounded flex items-center justify-center">
                                                    <span className="text-xs font-bold">T</span>
                                                </div>
                                                <span className="truncate">{caseData.investigated.social.tiktok}</span>
                                            </a>
                                        )}
                                        {caseData.investigated.social.linkedin && (
                                            <a 
                                                href={caseData.investigated.social.linkedin.startsWith('http') ? caseData.investigated.social.linkedin : `https://linkedin.com/in/${caseData.investigated.social.linkedin}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-2 text-sm text-blue-300 hover:text-blue-200 transition-colors"
                                            >
                                                <Linkedin className="w-5 h-5" />
                                                <span className="truncate">{caseData.investigated.social.linkedin}</span>
                                            </a>
                                        )}
                                        {caseData.investigated.social.altro && (
                                            <div className="flex items-center gap-2 text-sm text-white/60 col-span-2">
                                                <Globe className="w-5 h-5" />
                                                <span>{caseData.investigated.social.altro}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Case Images Section - Dopo Scheda Indagato (larghezza ridotta) */}
                    <div className="bg-[#0A0A0A] border border-white/10 p-6 w-full max-w-3xl">
                        <CaseImagesManager 
                            caseId={caseId} 
                            caseData={caseData} 
                            onUpdate={loadData}
                            isAdmin={isAdmin}
                        />
                    </div>

                    {/* Summary Stats */}
                    <div className="bg-[#0A0A0A] border border-white/10 p-6">
                        <h3 className="font-heading text-lg font-bold text-white mb-4 flex items-center gap-2">
                            <Clock className="w-5 h-5 text-[#00FF41]" />
                            Riepilogo Attività
                        </h3>
                        <div className="space-y-4">
                            <div className="bg-black/50 border border-white/10 p-4">
                                <p className="text-white/40 text-xs uppercase">Giornate di Attività</p>
                                <p className="text-3xl font-heading font-bold text-[#00FF41]">
                                    {activityDays.length}
                                </p>
                            </div>
                            <div className="bg-black/50 border border-white/10 p-4">
                                <p className="text-white/40 text-xs uppercase">Tempo Totale Servizio</p>
                                <p className="text-2xl font-heading font-bold text-white">
                                    {formatMinutesToHours(totalServiceMinutes)}
                                </p>
                            </div>
                            <div className="bg-black/50 border border-white/10 p-4">
                                <p className="text-white/40 text-xs uppercase">Max Operatori Impiegati</p>
                                <p className="text-2xl font-heading font-bold text-white flex items-center gap-2">
                                    <Users className="w-6 h-6 text-[#00FF41]" />
                                    {maxOperators}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Premises - Solo Admin */}
                {isAdmin && caseData.premises && (
                    <div className="bg-[#0A0A0A] border border-white/10 p-6 mb-8">
                        <h3 className="font-heading text-lg font-bold text-white mb-4">Premessa</h3>
                        <p className="text-white/80 whitespace-pre-wrap">{caseData.premises}</p>
                    </div>
                )}

                {/* Case Agent Manager - Solo Admin */}
                {isAdmin && (
                    <div className="mb-8">
                        <CaseAgentManager 
                            caseId={caseId} 
                            caseData={caseData} 
                            onUpdate={loadData}
                        />
                    </div>
                )}

                {/* Agent Hours Summary */}
                <div className="mb-8">
                    <AgentHoursSummary caseData={caseData} activityDays={activityDays} />
                </div>

                {/* Activity Days */}
                <div>
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="font-heading text-xl font-bold text-white">
                            Giornate di Attività
                        </h2>
                        <Button
                            onClick={() => setShowCreateDay(true)}
                            data-testid="add-activity-day-btn"
                            className="bg-[#00FF41] text-black hover:bg-[#00CC33] hover:shadow-neon rounded-none"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            NUOVA GIORNATA
                        </Button>
                    </div>

                    {activityDays.length === 0 ? (
                        <div className="bg-[#0A0A0A] border border-white/10 p-12 text-center">
                            <Calendar className="w-12 h-12 text-white/20 mx-auto mb-4" />
                            <p className="text-white/60">Nessuna giornata di attività registrata</p>
                            <Button
                                onClick={() => setShowCreateDay(true)}
                                className="mt-4 bg-[#00FF41] text-black hover:bg-[#00CC33] rounded-none"
                            >
                                Aggiungi la prima giornata
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {activityDays.map((day) => (
                                <Link
                                    key={day.id}
                                    to={`/cases/${caseId}/days/${day.id}`}
                                    data-testid={`activity-day-${day.id}`}
                                    className="block bg-[#0A0A0A] border border-white/10 hover:border-[#00FF41]/50 transition-all p-3 sm:p-4 group overflow-hidden"
                                >
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                        {/* Info principale */}
                                        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#00FF41]/10 border border-[#00FF41]/50 flex items-center justify-center flex-shrink-0">
                                                <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-[#00FF41]" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="font-mono text-[#00FF41] text-base sm:text-lg">
                                                    {formatDate(day.date)}
                                                </p>
                                                <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-1 text-white/60 text-xs sm:text-sm">
                                                    {day.service_start_time && day.service_end_time && (
                                                        <span className="flex items-center gap-1">
                                                            <Clock className="w-3 h-3" />
                                                            {day.service_start_time} - {day.service_end_time}
                                                        </span>
                                                    )}
                                                    <span className="flex items-center gap-1">
                                                        <Users className="w-3 h-3" />
                                                        {day.operators_count} op.
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Stats e freccia */}
                                        <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4 pl-13 sm:pl-0">
                                            <div className="flex items-center gap-3 sm:gap-6">
                                                <div className="text-center sm:text-right">
                                                    <p className="text-white/40 text-[10px] sm:text-xs uppercase">Eventi</p>
                                                    <p className="text-white font-mono text-sm">{day.events?.length || 0}</p>
                                                </div>
                                                <div className="text-center sm:text-right">
                                                    <p className="text-white/40 text-[10px] sm:text-xs uppercase">Media</p>
                                                    <p className="text-white font-mono text-sm">
                                                        {(day.media?.length || 0) + (day.events?.reduce((sum, event) => sum + (event.media?.length || 0), 0) || 0)}
                                                    </p>
                                                </div>
                                                {day.total_service_minutes && (
                                                    <div className="text-center sm:text-right">
                                                        <p className="text-white/40 text-[10px] sm:text-xs uppercase">Durata</p>
                                                        <p className="text-white font-mono text-sm whitespace-nowrap">
                                                            {formatMinutesToHours(day.total_service_minutes)}
                                                        </p>
                                                    </div>
                                                )}
                                                {day.transfer_time_minutes > 0 && (
                                                    <div className="text-center sm:text-right" data-testid="day-transfer-time">
                                                        <p className="text-white/40 text-[10px] sm:text-xs uppercase">Trasf.</p>
                                                        <p className="text-white font-mono text-sm whitespace-nowrap">
                                                            {formatMinutesToHours(day.transfer_time_minutes)}
                                                        </p>
                                                    </div>
                                                )}
                                                {(day.total_service_minutes || day.transfer_time_minutes) && (
                                                    <div className="text-center sm:text-right hidden sm:block" data-testid="day-total-time">
                                                        <p className="text-white/40 text-[10px] sm:text-xs uppercase">Totale</p>
                                                        <p className="text-[#00FF41] font-mono text-sm whitespace-nowrap font-bold">
                                                            {formatMinutesToHours((day.total_service_minutes || 0) + (day.transfer_time_minutes || 0))}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                            <ChevronRight className="w-5 h-5 text-white/40 group-hover:text-[#00FF41] transition-colors flex-shrink-0" />
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Modals */}
            {showCreateDay && (
                <CreateActivityDayModal
                    caseId={caseId}
                    existingDates={activityDays.map(d => d.date)}
                    onClose={() => setShowCreateDay(false)}
                    onCreated={handleDayCreated}
                />
            )}
            {showEditCase && (
                <EditCaseModal
                    caseData={caseData}
                    onClose={() => {
                        setShowEditCase(false);
                        // Ricarica il caso: le foto potrebbero essere state modificate
                        // anche senza un save esplicito del form
                        loadData();
                    }}
                    onUpdated={handleCaseUpdated}
                />
            )}
            
            {/* Delete Confirmation Dialog */}
            <DeleteConfirmDialog
                isOpen={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
                onConfirm={handleDeleteCase}
                caseNumber={caseData.protocol_number || caseData.case_number}
            />

            {/* Import da Smart Onboarding */}
            <ImportFromOnboardingDialog
                isOpen={showImportOnboarding}
                caseId={caseId}
                caseHasInvestigatedData={!!(caseData.investigated && (
                    caseData.investigated.name ||
                    caseData.investigated.birth_date ||
                    caseData.investigated.address ||
                    caseData.investigated.vehicle ||
                    caseData.investigated.workplace_company
                ))}
                onClose={() => setShowImportOnboarding(false)}
                onImported={loadData}
            />
        </div>
    );
}
